import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Checkbox,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { httpRequest } from 'src/services/httpRequest';
import { setUserDetails } from 'src/redux-store/slices/userSlice';

// Define RootState interface for TypeScript
interface RootState {
  user: {
    userDetails: {
      userName?: string;
      FirstName?: string;
      LastName?: string;
      EmailID?: string;
      UserRole?: string;
      accessToken?: string;
      refreshToken?: string;
      [key: string]: any;
    };
  };
}

interface LoginFormData {
  email: string;
  password: string;
}

function LoginPage() {
  const { state: locationState, township, uuid } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  });

  // Get user details from Redux store
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  // Redirect if already logged in
  useEffect(() => {
    if (userDetails.userName) {
      if (userDetails.UserRole === 'Admin') {
        navigate(
          `/${locationState}/${township}/${uuid}/bid-portal/Admin/home`,
          { replace: true }
        );
      } else {
        navigate(`/${locationState}/${township}/${uuid}/bid-portal/home`, {
          replace: true
        });
      }
    }
  }, []);

  // Autofill email when remember me is checked
  useEffect(() => {
    const savedRememberMe = Cookies.get('rememberMe');
    const savedEmail = Cookies.get('email');

    if (savedRememberMe === 'true' && savedEmail) {
      setFormData((prev) => ({
        ...prev,
        email: decodeURIComponent(savedEmail)
      }));
      setRememberMe(true);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const userResponse = await httpRequest(
        'POST',
        `/auth/login/${locationState}/${township}/${uuid}`,
        {
          EmailID: formData.email,
          Password: formData.password
        }
      );

      if (userResponse?.status === 'success') {
        if (rememberMe) {
          // Save credentials
          Cookies.set('rememberMe', 'true', { expires: 30 });
          Cookies.set('email', encodeURIComponent(formData.email), {
            expires: 30
          });
        } else {
          // Remove saved credentials
          Cookies.remove('rememberMe');
          Cookies.remove('email');
        }

        // Dispatch user details to Redux store
        // Preserve original email in userName for backward compatibility
        dispatch(
          setUserDetails({
            ...userResponse.data,
            id: userResponse.data.UserID,
            userName: userResponse.data.EmailID
          })
        );

        const isPasswordUpdatedByAdmin =
          userResponse.data?.IsPasswordUpdatedByAdmin === true;

        if (isPasswordUpdatedByAdmin) {
          // Force user to update password before accessing the app
          setForcePasswordChange(true);
          return;
        }

        const redirectPath = sessionStorage.getItem('redirectPath');

        if (redirectPath) {
          const finalPath = redirectPath.startsWith('/')
            ? redirectPath
            : `/${redirectPath}`;

          sessionStorage.removeItem('redirectPath');
          navigate(finalPath, { replace: false });
        } else {
          if (
            userResponse.data.UserRole === 'Admin' ||
            userResponse.data.UserRole === 'General Admin'
          ) {
            navigate(
              `/${locationState}/${township}/${uuid}/bid-portal/Admin/home`,
              { replace: true }
            );
          } else {
            navigate(`/${locationState}/${township}/${uuid}/bid-portal/home`, {
              replace: true
            });
          }
        }
      } else {
        setError(userResponse?.message);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate(`/${locationState}/${township}/${uuid}/forgot-password`);
  };

  const handleBackToHome = () => {
    navigate(`/${locationState}/${township}/${uuid}`);
  };

  const handleGoToChangePassword = () => {
    const role = userDetails?.UserRole;
    const basePath = `/${locationState}/${township}/${uuid}/bid-portal`;

    if (role === 'Admin' || role === 'General Admin') {
      navigate(`${basePath}/Admin/my-profile/change-password`, {
        replace: true,
        state: { fromAdminReset: true }
      });
    } else {
      navigate(`${basePath}/profile/change-password`, {
        replace: true,
        state: { fromAdminReset: true }
      });
    }

    setForcePasswordChange(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f5f5f5'
      }}
    >
      {/* Main Login Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Container maxWidth="sm">
          <Card
            sx={{
              borderRadius: '16px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              overflow: 'visible'
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <form onSubmit={handleSubmit}>
                <Box sx={{ p: 4, pb: 3 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      mb: 2
                    }}
                  >
                    Welcome Back
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mb: 3 }}
                  >
                    Enter your credentials to access your account
                  </Typography>

                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: '8px',
                        '& .MuiAlert-icon': { alignItems: 'center' }
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  <TextField
                    autoFocus
                    name="email"
                    label="Email Address"
                    type="email"
                    fullWidth
                    variant="outlined"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px'
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <Box
                          component="span"
                          sx={{
                            color: 'action.active',
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect width="20" height="16" x="2" y="4" rx="2" />
                            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                          </svg>
                        </Box>
                      )
                    }}
                  />

                  <TextField
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    variant="outlined"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px'
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <Box
                          component="span"
                          sx={{
                            color: 'action.active',
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect
                              width="18"
                              height="11"
                              x="3"
                              y="11"
                              rx="2"
                              ry="2"
                            />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                        </Box>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 3
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        id="rememberMe"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        sx={{
                          p: 0.5,
                          color: 'primary.main',
                          '&.Mui-checked': {
                            color: 'primary.main'
                          }
                        }}
                      />
                      <Typography
                        component="label"
                        htmlFor="rememberMe"
                        variant="body2"
                        sx={{ ml: 0.5, userSelect: 'none', cursor: 'pointer' }}
                      >
                        Remember me
                      </Typography>
                    </Box>

                    <Button
                      variant="text"
                      size="small"
                      onClick={handleForgotPassword}
                      sx={{
                        textTransform: 'none',
                        p: 0,
                        minWidth: 'auto',
                        fontWeight: 'medium',
                        fontSize: '0.8rem'
                      }}
                    >
                      Forgot password?
                    </Button>
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: 4,
                    pt: 0
                  }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    fullWidth
                    sx={{
                      borderRadius: '10px',
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Sign In'
                    )}
                  </Button>

                  <Button
                    onClick={handleBackToHome}
                    disabled={loading}
                    variant="text"
                    fullWidth
                    sx={{
                      mt: 2,
                      textTransform: 'none',
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Back To Home
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Force password change dialog when admin has reset the password */}
      <Dialog open={forcePasswordChange} disableEscapeKeyDown>
        <DialogTitle>Update Your Password</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            Your password was reset by an administrator. For security reasons,
            you must set a new password before you can continue using the
            portal.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleGoToChangePassword}
            variant="contained"
            color="primary"
          >
            Go to Update Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LoginPage;
